import { ForwardNumberFailure, type ActivateBusinessErrorDetail, type Country } from '@acs/shared';
import { Banner, Button, Icon } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';

/** Where an owner goes when something is on our side, not theirs. */
const SUPPORT_EMAIL = 'support@topcall24.com';

/**
 * Shown when a business could not be connected to our shared line.
 *
 * Nothing is bought any more (doc 18 D7): every business is served through a
 * shared number and told apart by the line its calls are diverted from, so
 * "activation" is handing out a slot on a number we already own. That leaves
 * exactly two honest things to say when it fails:
 *
 * - **The country has no shared line** (`not_served`) — we are not live there
 *   yet. Not retryable, and no promise of a number, because none is coming.
 * - **Anything else is ours** — a full or misconfigured pool, a transient
 *   error. Retryable, and the owner is told plainly it is not their fault.
 *
 * It replaces a card written for the purchase era, which split failures into
 * "no numbers in stock", "regulatory paperwork", "carrier side" and so on, and
 * ended every one of them with "we'll get a number assigned and email you". On
 * 2026-09-22 an owner saw exactly that — "Something went wrong on the carrier
 * side … We'll get a PL number assigned" — when the real cause was our own pool
 * entry having been deleted. Every word of it was wrong about what was
 * happening and about what would happen next.
 */
export function ForwardingUnavailable({
  businessName,
  country,
  error,
  onRetry,
  retrying,
}: {
  businessName: string;
  country: Country;
  error: unknown;
  onRetry: () => void;
  retrying: boolean;
}) {
  const { t } = useI18n();
  const { reason, country: failedCountry } = forwardNumberFailure(error, country);

  if (reason === 'not_served') {
    const subject = encodeURIComponent(`Waitlist — ${businessName} (${failedCountry})`);
    return (
      <div className="grid">
        <Banner tone="warning" title={t('numberUnavailable.not_served.headline')}>
          {t('numberUnavailable.not_served.detail')}
        </Banner>
        <div className="wizard-cta">
          <Button
            size="lg"
            variant="secondary"
            leadingIcon={<Icon name="mail" size={18} />}
            onClick={() => {
              window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}`;
            }}
          >
            {t('numberUnavailable.contactSupport')}
          </Button>
        </div>
      </div>
    );
  }

  const subject = encodeURIComponent(`Forwarding setup failed — ${businessName}`);
  return (
    <div className="grid">
      <Banner tone="danger" title={t('fwdUnavailable.headline', { business: businessName })}>
        {t('fwdUnavailable.detail')}
      </Banner>
      <div className="wizard-cta">
        <Button size="lg" loading={retrying} onClick={onRetry}>
          {t('wizard.tryAgain')}
        </Button>
        <Button
          variant="ghost"
          leadingIcon={<Icon name="mail" size={18} />}
          onClick={() => {
            window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}`;
          }}
        >
          {t('numberUnavailable.contactSupport')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Pull our typed reason out of a callable error. Firebase puts the `details`
 * payload from `HttpsError` on `error.details`; anything unrecognised — including
 * every error that never carried a reason — reads as `unknown`, which the card
 * above treats as "ours, retry".
 */
export function forwardNumberFailure(error: unknown, fallbackCountry: Country): ActivateBusinessErrorDetail {
  const details = (error as { details?: unknown })?.details as
    | Partial<ActivateBusinessErrorDetail>
    | undefined;
  const reason = ForwardNumberFailure.safeParse(details?.reason);
  return {
    reason: reason.success ? reason.data : 'unknown',
    country: (details?.country as Country) ?? fallbackCountry,
  };
}
